/**
 * UserDetailsModal Component
 *
 * Displays detailed information about a user including:
 * - User Details section (Name, Email Address, Role, Last Active Date)
 *
 * @see Figma designs:
 * - Admin/Owner view: node-id=1881-34859
 */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import LytModal from '../ui-core/modal/LytModal';
import LytButton from '../library/button/LytButton';
import LytIcon from '../library/icon/LytIcon';
import Spinner from '../library/spinner/Spinner';
import { ICON_NAMES } from '../library/icon/icon-constants';
import {
    applyTypography,
    TYPOGRAPHY_VARIANTS,
} from '../styled/theme/typography-utils';
import { formatRole } from '../../utils/role-utils';
import {
    getTeamMemberDetail,
    getLytTeamMemberDetail,
} from '../../services/requests/users-and-permissions/users/users';
import { TEAM_CONTEXT } from './index';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
`;
const UserInfoSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 24px;
`;
const SectionHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
`;
const SectionTitle = styled.h3`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_BOLD)}
    letter-spacing: 0.14px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const SectionDescription = styled.p`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.default};
    margin: 0;
`;
const InfoGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;
const InfoRow = styled.div`
    display: flex;
    align-items: center;
    gap: 24px;
    height: 26px;
`;
const InfoLabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    width: 120px;
`;
const IconWrapper = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;
const InfoLabel = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_0_MEDIUM)}
    line-height: 16px;
    color: ${({ theme }) => theme?.text?.subtle};
    padding: 5px 0;
`;
const InfoValue = styled.span`
    ${applyTypography(TYPOGRAPHY_VARIANTS.SIZE_1_NORMAL)}
    line-height: 18px;
    color: ${({ theme }) => theme?.text?.subtle};
    flex: 1;
    min-width: 0;
    padding: 4px 0;
`;
const LoadingContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px 0;
`;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Format a date string for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date string (e.g., "Jan 15, 2024")
 */
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * UserDetailsModal - Displays detailed user information
 *
 * For viewer roles: shows user info from table data (no API call)
 * For admin/owner/lyt roles: fetches detail from API to include changelog data
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.closeModal - Function to close modal with result (from ModalProvider)
 * @param {Object} props.user - User object containing user data (from table row)
 * @param {boolean} props.isAdmin - Whether the current viewer is an admin/owner/lyt user
 * @param {string} props.agencyId - The agency ID for the detail API call
 */
const UserDetailsModal = ({
    open = false,
    onClose = undefined,
    closeModal = undefined,
    user = {},
    isAdmin = false,
    agencyId = undefined,
    teamContext = undefined,
}) => {
    const [loading, setLoading] = useState(false);
    const [detailData, setDetailData] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    // Fetch detail data for admin users when modal opens
    useEffect(() => {
        if (!open || !isAdmin) return;
        const userEmail = user?.email;
        if (!userEmail) return;
        let cancelled = false;
        setLoading(true);
        setFetchError(null);

        // Use the LYT-specific endpoint when opened from the LYT Team Management page
        const detailPromise =
            teamContext === TEAM_CONTEXT.LYT
                ? getLytTeamMemberDetail({
                      userEmail,
                  })
                : getTeamMemberDetail({
                      userEmail,
                      agencyId,
                  });
        detailPromise
            .then((response) => {
                if (!cancelled) {
                    setDetailData(response?.data || response);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error('Failed to fetch user detail:', err);
                    setFetchError(err);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, isAdmin, user?.email, agencyId, teamContext]);
    if (!open) return null;

    // Use detail data when available (admin flow), otherwise fall back to table row data
    const source = detailData || user;
    const userEmail = source?.email || 'N/A';
    const userRole = source?.role || source?.usertype || 'Unknown';
    const lastActiveDate = formatDate(
        source?.last_login_at_ts || source?.last_login || source?.lastLoginAt
    );

    // Handle close action
    const handleClose = () => {
        closeModal?.({
            dismissed: true,
        }) || onClose?.();
    };

    // Modal footer actions
    const footerActions = (
        <LytButton type="secondary" onClick={handleClose}>
            Close
        </LytButton>
    );
    return (
        <LytModal
            open={open}
            title="User Details"
            onClose={handleClose}
            actions={footerActions}
            size="medium">
            <ModalContent>
                {loading ? (
                    <LoadingContainer>
                        <Spinner size="medium" />
                    </LoadingContainer>
                ) : (
                    <UserInfoSection>
                        <SectionHeader>
                            <SectionTitle>User Details</SectionTitle>
                            <SectionDescription>
                                See information about the user.
                            </SectionDescription>
                        </SectionHeader>
                        <InfoGrid>
                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.StackedEmailFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>Email Address</InfoLabel>
                                </InfoLabelContainer>
                                <InfoValue>{userEmail}</InfoValue>
                            </InfoRow>

                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.BriefcaseFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>Role</InfoLabel>
                                </InfoLabelContainer>
                                <InfoValue>{formatRole(userRole)}</InfoValue>
                            </InfoRow>

                            <InfoRow>
                                <InfoLabelContainer>
                                    <IconWrapper>
                                        <LytIcon
                                            name={ICON_NAMES.CalendarTodayFill}
                                            size={16}
                                        />
                                    </IconWrapper>
                                    <InfoLabel>Last Active Date</InfoLabel>
                                </InfoLabelContainer>
                                <InfoValue>{lastActiveDate}</InfoValue>
                            </InfoRow>
                        </InfoGrid>
                    </UserInfoSection>
                )}
            </ModalContent>
        </LytModal>
    );
};
UserDetailsModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    closeModal: PropTypes.func,
    user: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        email: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        usertype: PropTypes.string,
        role: PropTypes.string,
        last_login: PropTypes.string,
        last_login_at_ts: PropTypes.string,
        created_at: PropTypes.string,
        created_at_ts: PropTypes.string,
        jurisdictions: PropTypes.array,
        locations: PropTypes.array,
    }),
    isAdmin: PropTypes.bool,
    agencyId: PropTypes.string,
    teamContext: PropTypes.string,
};
export default UserDetailsModal;
